import React, { useState, useEffect } from 'react';
import { Card, Table, Select, Typography, Row, Col, Avatar, App } from 'antd';
import { TrophyOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { apiClient, Brand } from '../services/api';
import { useTranslation } from 'react-i18next';

const { Title } = Typography;
const { Option } = Select;

interface RankedBrand extends Brand {
  score: number;
  rank: number;
}

const getDimensionOptions = (t: any) => [
  { value: 'overall', label: t('rankings.dimension_overall') },
  { value: 'environmental', label: t('rankings.dimension_environmental') },
  { value: 'laborRights', label: t('rankings.dimension_laborRights') },
  { value: 'animalWelfare', label: t('rankings.dimension_animalWelfare') },
  { value: 'transparency', label: t('rankings.dimension_transparency') },
  {
    value: 'socialResponsibility',
    label: t('rankings.dimension_socialResponsibility'),
  },
  { value: 'carbonFootprint', label: t('rankings.dimension_carbonFootprint') },
];

const RankingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [selectedDimension, setSelectedDimension] = useState('overall');
  const [rankedBrands, setRankedBrands] = useState<RankedBrand[]>([]);
  const [loading, setLoading] = useState(false);
  const { message } = App.useApp();

  useEffect(() => {
    loadRankings();
  }, [selectedDimension]);

  const loadRankings = async () => {
    setLoading(true);
    try {
      // 获取所有品牌
      const brandsResponse = await apiClient.getBrands({ limit: 100 });
      const brands = brandsResponse.data;

      // 获取每个品牌的评分
      const rankedBrandsData: RankedBrand[] = [];

      for (const brand of brands) {
        try {
          const ratingResponse = await apiClient.getBrandRating(brand.id);
          const rating = ratingResponse.data;

          let score = 0;
          if (selectedDimension === 'overall') {
            score = rating.overallScore || 0;
          } else {
            const dimension =
              rating.dimensions[
                selectedDimension as keyof typeof rating.dimensions
              ];
            score = dimension?.score || 0;
          }

          rankedBrandsData.push({
            ...brand,
            score,
            rank: 0, // 将在排序后设置
          });
        } catch (error) {
          // 如果品牌没有评分，设置默认分数
          rankedBrandsData.push({
            ...brand,
            score: 0,
            rank: 0,
          });
        }
      }

      // 按分数排序并设置排名
      rankedBrandsData.sort((a, b) => b.score - a.score);
      rankedBrandsData.forEach((brand, index) => {
        brand.rank = index + 1;
      });

      setRankedBrands(rankedBrandsData);
    } catch (error) {
      message.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const getRankIcon = (rank: number) => {
    if (rank === 1)
      return <TrophyOutlined style={{ color: '#FFD700', fontSize: '20px' }} />;
    if (rank === 2)
      return <TrophyOutlined style={{ color: '#C0C0C0', fontSize: '18px' }} />;
    if (rank === 3)
      return <TrophyOutlined style={{ color: '#CD7F32', fontSize: '16px' }} />;
    return <span style={{ fontSize: '16px', fontWeight: 'bold' }}>{rank}</span>;
  };

  const getScoreColor = (score: number) => {
    if (score >= 4) return '#52c41a';
    if (score >= 3) return '#faad14';
    if (score >= 2) return '#fa8c16';
    return '#ff4d4f';
  };

  const columns = [
    {
      title: t('rankings.col_rank'),
      dataIndex: 'rank',
      key: 'rank',
      width: 80,
      render: (rank: number) => (
        <div style={{ textAlign: 'center' }}>{getRankIcon(rank)}</div>
      ),
    },
    {
      title: t('rankings.col_brand'),
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record: RankedBrand) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Avatar
            src={record.logo}
            size={40}
            style={{ backgroundColor: '#f0f0f0' }}
          >
            {name.charAt(0)}
          </Avatar>
          <div>
            <div
              style={{ fontWeight: 'bold', cursor: 'pointer' }}
              onClick={() => navigate(`/brands/${record.id}`)}
            >
              {name}
            </div>
            <div style={{ fontSize: '12px', color: '#666' }}>
              {record.categories?.join(', ')}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: t('rankings.col_score'),
      dataIndex: 'score',
      key: 'score',
      width: 120,
      render: (score: number) => (
        <div style={{ textAlign: 'center' }}>
          <div
            style={{
              fontSize: '18px',
              fontWeight: 'bold',
              color: getScoreColor(score),
            }}
          >
            {score.toFixed(1)}
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>/ 5.0</div>
        </div>
      ),
    },
    {
      title: t('rankings.col_founded'),
      dataIndex: 'founded',
      key: 'founded',
      width: 100,
      render: (founded: string) => {
        const year = founded ? new Date(founded).getFullYear() : '';
        return <span>{year}</span>;
      },
    },
    {
      title: t('rankings.col_headquarters'),
      dataIndex: 'headquarters',
      key: 'headquarters',
      width: 150,
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Row gutter={24}>
        <Col span={24}>
          <Card>
            <div style={{ marginBottom: '24px' }}>
              <Title level={2} style={{ marginBottom: '16px' }}>
                <TrophyOutlined style={{ marginRight: '8px' }} />
                {t('rankings.title')}
              </Title>
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '16px' }}
              >
                <span>{t('rankings.sort_dimension')}</span>
                <Select
                  value={selectedDimension}
                  onChange={setSelectedDimension}
                  style={{ width: 200 }}
                  size="large"
                >
                  {getDimensionOptions(t).map(option => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
              </div>
            </div>

            <Table
              columns={columns}
              dataSource={rankedBrands}
              loading={loading}
              rowKey="id"
              pagination={{
                pageSize: 20,
                showSizeChanger: false,
                showQuickJumper: true,
                showTotal: (total, range) =>
                  t('rankings.pagination_total', {
                    start: range[0],
                    end: range[1],
                    total,
                  }),
              }}
              rowClassName={record => {
                if (record.rank <= 3) return 'top-rank-row';
                return '';
              }}
            />
          </Card>
        </Col>
      </Row>

      <style>{`
        .top-rank-row {
          background-color: #fffbe6;
        }
        .top-rank-row:hover {
          background-color: #fff7e6 !important;
        }
      `}</style>
    </div>
  );
};

export default RankingsPage;
